import { Component, HostListener, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { StreamsSubscribeDrawerService } from './streams-subscribe-drawer.service';
import { QuickBuild } from 'src/app/people/quick-build.service';
import { NotificationService } from '../notification-service/notification.service';
import { Router } from '@angular/router';
import { AlertType } from '../alert/alert.component';
import { Subscription } from 'rxjs';
import { Log } from 'src/app/logger.service';
import { MatDialog } from '@angular/material/dialog';
import {
  DownloadsService,
  DownloadFormat,
  DOWNLOAD_FORMATS,
  DownloadType,
} from '../../downloads/downloads.service';
import { DownloadStatusService } from '../download-status/download-status.service';
import {
  CreditUsageService,
  CreditUsage,
} from '../credit-usage-indicator/credit-usage-service.service';
import { SubscriptionDialogComponent } from 'src/app/settings/billing/subscription-dialog/subscription-dialog.component';
import { PeopleReportService } from 'src/app/people/report-service.service';

@Component({
  selector: 'app-streams-subscribe-drawer',
  templateUrl: './streams-subscribe-drawer.component.html',
  styleUrls: ['./streams-subscribe-drawer.component.scss'],
  animations: [
    trigger('drawerAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('300ms ease-out', style({ transform: 'translateX(0)' })),
      ]),
      transition(':leave', [animate('300ms ease-in', style({ transform: 'translateX(100%)' }))]),
    ]),
    trigger('backdropAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('300ms ease-in', style({ opacity: 0 }))]),
    ]),
    trigger('overlayAnimation', [
      transition(':leave', [animate('300ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
  providers: [],
  standalone: false,
})
export class StreamsSubscribeDrawerComponent implements OnChanges, OnInit, OnDestroy {
  isOpen = false;
  title = 'Data to Download';
  quickBuild: QuickBuild;
  mode: 'filters' | 'data-selection' = 'filters';
  matchingRecords: number | null = null;

  selectedOptions = {
    download: true,
    subscribe: false,
  };

  selectedFormat: DownloadType = DownloadType.MOST_RECENT_JOBS_CSV;

  alertType = AlertType;
  private serviceSubscription: Subscription;
  currentCreditUsage: CreditUsage | null = null;
  downloadLimit: number | undefined = undefined;

  formats: DownloadFormat[] = DOWNLOAD_FORMATS;
  customColumns: string[] | null = null;
  suggestedFilename: string | null = null;

  constructor(
    private drawerService: StreamsSubscribeDrawerService,
    private downloadsService: DownloadsService,
    private notify: NotificationService,
    private router: Router,
    private downloadStatusService: DownloadStatusService,
    private creditUsageService: CreditUsageService,
    private dialog: MatDialog,
    private peopleReportService: PeopleReportService
  ) {}

  @HostListener('document:keydown.escape')
  onEscape() {
    this.dismiss();
  }

  ngOnInit() {
    // Subscribe to drawer open/close state
    this.serviceSubscription = this.drawerService.isDrawerOpen$.subscribe((isOpen) => {
      this.isOpen = isOpen;
    });

    // Subscribe to drawer configuration
    this.serviceSubscription.add(
      this.drawerService.drawerConfig$.subscribe((config) => {
        this.mode = config.mode;
        if (config.quickBuild) {
          this.quickBuild = config.quickBuild;
        }
        this.matchingRecords = config.matchingRecords;
        if (config.title) {
          this.title = config.title;
        }
        this.customColumns = config.customColumns;
        this.suggestedFilename = config.suggestedFilename;
        this.buildFormats();
        this.calculateDownloadLimit();

        // Fetch actual count from the API when opening in data-selection mode with a quickbuild
        if (config.mode === 'data-selection' && this.quickBuild?.rootFilterGroup) {
          this.fetchMatchingRecords();
        }

        Log.d('StreamsSubscribeDrawerComponent ngOnInit matchingRecords', this.matchingRecords);
      })
    );
  }

  ngOnChanges(changes: any) {
    if ('quickBuild' in changes) {
      this.quickBuild = changes.quickBuild.currentValue;
    }
    Log.d('streams-subscribe-drawer Changes detected', changes);
  }

  ngOnDestroy() {
    if (this.serviceSubscription) {
      this.serviceSubscription.unsubscribe();
    }
    this.drawerService.closeDrawer();
  }

  dismiss() {
    this.drawerService.closeDrawer();
    this.mode = 'filters';
    this.selectedOptions = {
      download: true,
      subscribe: false,
    };
    this.customColumns = null;
    this.suggestedFilename = null;
    this.buildFormats();
    this.selectedFormat = this.formats[0].value;
    this.matchingRecords = 0;
  }

  private fetchMatchingRecords() {
    this.serviceSubscription.add(
      this.peopleReportService
        .getPeopleData(this.quickBuild, true, undefined, 0, undefined)
        .subscribe({
          next: (data: any) => {
            this.matchingRecords = data.count || 0;
            this.calculateDownloadLimit();
            Log.d('StreamsSubscribeDrawerComponent fetchMatchingRecords', this.matchingRecords);
          },
          error: (err: any) => {
            Log.e('Error fetching matching records count:', err);
          },
        })
    );
  }

  private buildFormats() {
    if (this.customColumns && this.customColumns.length > 0) {
      const customFormat: DownloadFormat = {
        value: DownloadType.CUSTOM_CSV,
        label: 'Custom CSV',
        fileTypeDescriptor: 'CSV',
        description: `AI-curated CSV with ${this.customColumns.length} columns selected for this query.`,
        requestParams: {
          return_fields: this.customColumns,
        },
        featureDescriptions: [
          { key: 'fileFormat', description: 'CSV', indicator: 'full' },
          { key: 'columnCount', description: `${this.customColumns.length} columns`, indicator: 'full' },
          { key: 'excelCompatible', description: 'Yes', indicator: 'full' },
          { key: 'machineReadable', description: 'Yes', indicator: 'full' },
          { key: 'bestFor', description: 'This specific query', indicator: 'full' },
        ],
      };
      this.formats = [customFormat, ...DOWNLOAD_FORMATS];
      this.selectedFormat = DownloadType.CUSTOM_CSV;
    } else {
      this.formats = DOWNLOAD_FORMATS;
      if (!this.formats.some((f) => f.value === this.selectedFormat)) {
        this.selectedFormat = this.formats[0].value;
      }
    }
  }

  finalize() {
    if (this.mode === 'filters') {
      this.mode = 'data-selection';
      this.calculateDownloadLimit();
    } else {
      this.completePurchase();
    }
  }

  private calculateDownloadLimit() {
    this.creditUsageService.creditUsage$.subscribe({
      next: (usage) => {
        this.currentCreditUsage = usage;
        if (usage?.peopleQuota) {
          this.downloadLimit = usage.peopleQuota - (usage.peopleUsage || 0);
        } else {
          this.downloadLimit = undefined;
        }
      },
    });
  }

  goBack() {
    this.mode = 'filters';
  }

  selectFormat(format: DownloadFormat['value']) {
    this.selectedFormat = format;
  }

  hasSelectedOption(): boolean {
    return this.selectedOptions.download || this.selectedOptions.subscribe;
  }

  getOrgId(): string {
    return this.router.url.split('/')[1];
  }

  // Get the downloads page URL with the organization ID
  getDownloadsUrl(): string {
    return `/${this.getOrgId()}/downloads`;
  }

  private completePurchase() {
    // Create a name for the download based on the filter criteria
    const downloadName =
      this.title.length > 0
        ? `${this.title} - ${new Date().toLocaleDateString()}`
        : `Data Export - ${new Date().toLocaleDateString()}`;

    // Check if no options are selected
    if (!this.hasSelectedOption()) {
      this.notify.error('Please select at least one option: Download or Subscribe');
      return;
    }

    // Process download option if selected
    if (this.selectedOptions.download) {
      let limit = this.downloadLimit;
      if (limit && limit > 10000) {
        limit = 10000;
      }
      const selectedFormatObj = this.formats.find((f) => f.value === this.selectedFormat);
      const customParams =
        this.selectedFormat === DownloadType.CUSTOM_CSV
          ? selectedFormatObj?.requestParams
          : undefined;
      this.downloadsService
        .addDownload(this.quickBuild, this.selectedFormat, limit, customParams, this.suggestedFilename || undefined)
        .subscribe({
        next: (id) => {
          if (!id) {
            this.notify.error('Failed to start download');
            Log.e('Failed to start download: no delivery ID received', id);
            return;
          }
          this.downloadStatusService.showDownloadStatus(id);
        },
        error: (error) => {
          Log.e('Error starting download', error);
          this.notify.error('Failed to start download. Please try again.');
        },
      });
    }

    this.dismiss();
  }

  isButtonDisabled(): boolean {
    return (
      this.mode === 'data-selection' &&
      (!this.hasSelectedOption() || !this.matchingRecords || this.matchingRecords <= 0)
    );
  }

  getTooltipMessage(): string {
    if (this.mode !== 'data-selection') {
      return '';
    }

    if (!this.hasSelectedOption()) {
      return 'Please select at least one option to continue';
    }

    if (!this.matchingRecords || this.matchingRecords <= 0) {
      return 'No matching records found. Try adjusting your filters.';
    }

    return '';
  }

  openUpgradeModal(): void {
    const dialogRef = this.dialog.open(SubscriptionDialogComponent, {
      width: '600px',
      backdropClass: 'darker-dialog-backdrop',
      panelClass: 'full-w-mobile-dialog',
      data: { subscriptionType: 'people' },
    });

    dialogRef.afterClosed().subscribe(() => {
      // Always refresh credit usage after dialog closes
      this.calculateDownloadLimit();
    });
  }
}
